import React from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldAlert, AlertTriangle, Webhook, Bell, Zap, Route } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { NOTIFICATION_CONTAINERS } from '../components/NotificationPageLayout';

const placeholder = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/u81l6p9k_FOR%20NOTIFICATIONS.png';

const topics = [
  {
    id: 'confirmation',
    icon: CheckCircle2,
    title: 'Confirmation Notification',
    tagline: 'Transaction completion alerts',
    description:
      "Receive instant confirmation when payments are successfully processed. Essential for order fulfilment, inventory management and customer communication automation.",
    accent: 'from-emerald-600/15 to-teal-500/10',
    iconColor: 'text-emerald-700',
  },
  {
    id: 'chargeback',
    icon: ShieldAlert,
    title: 'Chargeback Notification',
    tagline: 'Dispute and chargeback alerts',
    description:
      "Get immediate notification when chargebacks are initiated against your transactions. Critical for timely dispute response and account protection.",
    accent: 'from-amber-600/15 to-orange-500/10',
    iconColor: 'text-amber-700',
  },
  {
    id: 'fraud',
    icon: AlertTriangle,
    title: 'Fraud Notification',
    tagline: 'Security and risk alerts',
    description:
      "Receive alerts for suspicious activity, fraud attempts and risk management events. Enables rapid response to potential security threats and account protection.",
    accent: 'from-rose-600/15 to-red-500/10',
    iconColor: 'text-rose-700',
  },
];

const Notifications = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" data-testid="notifications-page">
      <Header />
      <div className="h-20" />

      {/* Hero with full-page placeholder background */}
      <section
        className="relative flex-1 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${placeholder})` }}
      >
        {/* Dark overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1929]/88 via-[#0a1929]/80 to-[#163a5f]/85" />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-14">
          <button
            onClick={() => navigate('/technical-documentation')}
            className="flex items-center gap-2 text-white/85 hover:text-white font-medium mb-8 transition-colors duration-200"
            data-testid="notifications-back-button"
          >
            <ArrowLeft size={18} />
            Back to Technical Documentation
          </button>

          {/* Intro block */}
          <div className="mb-10 max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/40 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.15em] mb-5" data-testid="notifications-eyebrow">
              <Bell size={13} />
              Real-time event webhooks
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight" data-testid="notifications-title">
              Notifications
            </h1>
            <p className="text-lg text-white/85 leading-relaxed max-w-3xl">
              Our notification system keeps you informed of critical payment events in real-time. From successful transactions to fraud alerts, these automated notifications ensure you're always aware of important activity on your merchant account.
            </p>
          </div>

          {/* How Notifications Work + Implementation Guide (two-up) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10 max-w-5xl">
            <div className="rounded-lg bg-white/10 backdrop-blur-md border border-white/15 p-6" data-testid="how-it-works-card">
              <div className="flex items-center gap-2 mb-3">
                <Webhook size={18} className="text-[#ef5b25]" />
                <h2 className="text-lg font-bold text-white">How Notifications Work</h2>
              </div>
              <p className="text-sm text-white/75 leading-relaxed">
                Notifications are sent via <code className="bg-white/10 px-1.5 py-0.5 rounded text-xs">HTTP POST</code> to your specified endpoint URL, allowing your system to respond immediately to payment events. Each notification type includes specific data relevant to the event, enabling automated processing and business logic implementation.
              </p>
            </div>
            <div className="rounded-lg bg-white/10 backdrop-blur-md border border-white/15 p-6" data-testid="implementation-guide-card">
              <div className="flex items-center gap-2 mb-3">
                <Zap size={18} className="text-[#ef5b25]" />
                <h2 className="text-lg font-bold text-white">Implementation Guide</h2>
              </div>
              <p className="text-sm text-white/75 leading-relaxed">
                Configure your notification endpoints to receive, validate and process these alerts. Each notification type below includes detailed specifications for the data format, required responses and recommended handling procedures.
              </p>
            </div>
          </div>

          {/* 3 Notification Containers — row of 3 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="notification-topic-grid">
            {topics.map((t) => {
              return (
                <Link
                  key={t.id}
                  to={`/technical-documentation/notifications/${t.id}`}
                  className="group relative overflow-hidden bg-white/95 backdrop-blur-sm rounded-lg shadow-xl p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`notification-topic-${t.id}`}
                >
                  {/* Accent gradient */}
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${t.accent.replace('/15', '').replace('/10', '')} bg-[#1F4E79]`} />
                  <div className={`absolute -top-6 -right-6 w-28 h-28 rounded-full blur-2xl bg-gradient-to-br ${t.accent} opacity-60 pointer-events-none`} />

                  <div className="relative">
                    <p className={`text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-1`}>{t.tagline}</p>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#1F4E79] transition-colors">
                      {t.title}
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed mb-4">{t.description}</p>
                    <span className="inline-flex items-center gap-1 text-[#1F4E79] font-semibold text-xs group-hover:gap-2 transition-all">
                      View specification
                      <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Dispatch Matrix — apisignature → documentation page */}
          <div className="mt-10 rounded-lg bg-white/10 backdrop-blur-md border border-white/15 overflow-hidden" data-testid="dispatch-matrix">
            <div className="flex items-center gap-2 px-5 py-3 bg-white/5 border-b border-white/10">
              <Route size={16} className="text-[#ef5b25]" />
              <h2 className="text-sm font-bold text-white tracking-wide">
                Dispatch Matrix <span className="text-white/60 font-normal">— route incoming notifications by <code className="bg-white/10 px-1.5 py-0.5 rounded text-[11px] font-mono">apisignature</code></span>
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    <th className="text-left p-3 text-xs font-bold text-white/70 uppercase tracking-wider w-64">apisignature value</th>
                    <th className="text-left p-3 text-xs font-bold text-white/70 uppercase tracking-wider">Route to</th>
                    <th className="text-left p-3 text-xs font-bold text-white/70 uppercase tracking-wider">Shared URL with</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid="dispatch-row-confirmation">
                    <td className="p-3">
                      <code className="inline-block px-2 py-1 bg-black/40 text-[#85ea2d] font-mono text-xs rounded border border-white/10">
                        (none — standalone URL)
                      </code>
                    </td>
                    <td className="p-3">
                      <Link to="/technical-documentation/notifications/confirmation" className="text-[#ef5b25] hover:text-[#ffcba6] font-semibold underline">
                        Confirmation Notification
                      </Link>
                    </td>
                    <td className="p-3 text-white/60 text-xs">Dedicated Notification URL</td>
                  </tr>
                  <tr className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid="dispatch-row-chargeback">
                    <td className="p-3">
                      <code className="inline-block px-2 py-1 bg-black/40 text-[#85ea2d] font-mono text-xs rounded border border-white/10">
                        chargeback
                      </code>
                    </td>
                    <td className="p-3">
                      <Link to="/technical-documentation/notifications/chargeback" className="text-[#ef5b25] hover:text-[#ffcba6] font-semibold underline">
                        Chargeback Notification
                      </Link>
                    </td>
                    <td className="p-3 text-white/60 text-xs">Shared URL with Fraud</td>
                  </tr>
                  <tr className="hover:bg-white/5 transition-colors" data-testid="dispatch-row-fraud">
                    <td className="p-3">
                      <code className="inline-block px-2 py-1 bg-black/40 text-[#85ea2d] font-mono text-xs rounded border border-white/10">
                        fraudtransactionnotification
                      </code>
                    </td>
                    <td className="p-3">
                      <Link to="/technical-documentation/notifications/fraud" className="text-[#ef5b25] hover:text-[#ffcba6] font-semibold underline">
                        Fraud Notification
                      </Link>
                    </td>
                    <td className="p-3 text-white/60 text-xs">Shared URL with Chargeback</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="px-5 py-3 bg-white/5 border-t border-white/10 text-xs text-white/55 leading-relaxed">
              Implementation tip: switch on <code className="bg-white/10 px-1 py-0.5 rounded">$_POST["apisignature"]</code> (PHP) or the <code className="bg-white/10 px-1 py-0.5 rounded">apisignature</code> parameter (C#) at the top of your Chargeback-Fraud handler to dispatch to the right branch.
            </p>
          </div>

          {/* Tiny hint to aid developers */}
          <p className="text-xs text-white/55 mt-8 max-w-3xl" data-testid="notifications-tip">
            <strong className="text-white/75">Tip:</strong> the order of registered notification guide is{' '}
            {NOTIFICATION_CONTAINERS.map((c, i) => (
              <span key={c.id}>
                {i > 0 && ' · '}
                <Link to={c.link} className="text-white/85 hover:text-white underline decoration-white/30 hover:decoration-white/70 decoration-1 underline-offset-2">{c.title}</Link>
              </span>
            ))}
            .
          </p>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};


export default Notifications;
